<script setup lang="ts">
// Vendor
import type { ComponentPublicInstance } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { array, date, number, object, string, z } from "zod";

// Constants
import { SALESFORCE_FORM_TYPE } from "@citizenm/salesforce/constants/form";

// Utils
import { formatDate } from "@citizenm/foundation/utils/date";

// Components
// TODO: remove this import when legacy FormRow component is removed
import BaseForm from "@citizenm/form/components/BaseForm.vue";
import FormRow from "@citizenm/form/components/FormRow.vue";

const { hotels = [], thankYouPage } = defineProps<{
  hotels?: FormChoiceEntry<string>[];
  thankYouPage?: RouteLocationRaw;
}>();

const { handleSubmit, state } = useSalesforceForm(
  SALESFORCE_FORM_TYPE.webToCase,
  thankYouPage,
);
const { t } = useI18n();

const GROUPS_MIN_REQUIRED_ROOMS = 11;

const validationObject = object({
  Hotel_Location__c: string({
    required_error: t("website.form.requiredValue"),
  }),
  Date_from__c: date({
    required_error: t("website.form.requiredValue"),
  }),
  Group_Date_to__c: date({
    required_error: t("website.form.requiredValue"),
  }).refine(
    (dateTo) => {
      const { Date_from__c } = groupsForm.value?.$.exposed?.values || {};

      return !(Date_from__c && dateTo <= Date_from__c);
    },
    {
      message: t("website.form.groups.end_date.error"),
    },
  ),
  Groups_Number_of_rooms__c: number({
    required_error: t("website.form.requiredValue"),
  }).min(GROUPS_MIN_REQUIRED_ROOMS, {
    message: t("website.form.groups.number_of_rooms.min_error", {
      min: GROUPS_MIN_REQUIRED_ROOMS,
    }),
  }),
  Groups_Number_of_guests__c: number({
    required_error: t("website.form.requiredValue"),
  }),
  subject: string().optional(),
  description: string().optional(),
  name: string({
    required_error: t("website.form.requiredValue"),
  }),
  company: string({
    required_error: t("website.form.requiredValue"),
  }),
  phone: string({
    required_error: t("website.form.requiredValue"),
  }),
  email: string({
    required_error: t("website.form.requiredValue"),
  }).email(t("website.form.error.email_invalid")),
  Meeting_rooms_needed__c: array(string()).optional(),
  MeetingroomInformation__c: string().optional(),
  CreatedFromWebform__c: string().default("1"),
  privacySelection: string().optional(),
});

const validationSchema = toTypedSchema(validationObject);

const groupsForm = ref<ComponentPublicInstance<typeof BaseForm> | null>(null);

const showMeetingRoomInformationField = computed(() =>
  groupsForm.value?.$.exposed?.values?.Meeting_rooms_needed__c?.includes("yes"),
);

const meetingRoomsNeededOptions = [
  {
    label: t("website.form.groups.meeting_rooms_needed.label"),
    value: "yes",
  },
];

const privacyOptions = [
  {
    label: t("website.form.groups.privacy.option_1"),
    value: "GroupsPromotionalOffers__c",
  },
  {
    label: t("website.form.groups.privacy.option_2"),
    value: "GroupsNewsletters__c",
  },
  {
    label: t("website.form.groups.privacy.option_3"),
    value: "GroupsNoPromotions__c",
  },
];

function handleGroupsFormSubmit(
  values: Partial<typeof validationObject._type>,
) {
  const {
    privacySelection,
    Date_from__c,
    Group_Date_to__c,
    Meeting_rooms_needed__c,
    ...rest
  } = values;

  handleSubmit({
    ...rest,
    Meeting_rooms_needed__c: Meeting_rooms_needed__c?.includes("yes")
      ? "1"
      : "0",
    [String(privacySelection)]: "1",
    Date_from__c: Date_from__c
      ? formatDate(Date_from__c, "dd/MM/yyyy")
      : undefined,
    Group_Date_to__c: Group_Date_to__c
      ? formatDate(Group_Date_to__c, "dd/MM/yyyy")
      : undefined,
  });
}
</script>

<template>
  <BaseForm
    ref="groupsForm"
    :state="state"
    :validation-schema="validationSchema"
    @submit="handleGroupsFormSubmit"
  >
    <FormSection :title="t('website.form.groups.requirements.title')">
      <FormRow template="1fr 1fr">
        <FormControl
          :label="t('website.form.groups.hotel.label')"
          name="Hotel_Location__c"
        >
          <FormSelect
            :options="hotels"
            :placeholder="t('website.form.groups.hotel.placeholder')"
          />
        </FormControl>
      </FormRow>
      <FormRow template="1fr 1fr">
        <FormControl
          :label="t('website.form.groups.start_date.label')"
          name="Date_from__c"
        >
          <FormDate />
        </FormControl>
        <FormControl
          :label="t('website.form.groups.end_date.label')"
          name="Group_Date_to__c"
        >
          <FormDate />
        </FormControl>
      </FormRow>
      <FormRow template="1fr 1fr">
        <FormControl
          :label="t('website.form.groups.number_of_rooms.label')"
          name="Groups_Number_of_rooms__c"
        >
          <FormText type="number" />
        </FormControl>
        <FormControl
          :label="t('website.form.groups.number_of_guests.label')"
          name="Groups_Number_of_guests__c"
        >
          <FormText type="number" />
        </FormControl>
      </FormRow>
    </FormSection>
    <FormSection>
      <FormRow template="1fr 1fr">
        <FormControl
          :label="t('website.form.groups.subject.label')"
          name="subject"
        >
          <FormText />
        </FormControl>
      </FormRow>
      <FormRow>
        <FormControl
          :hint="t('website.form.groups.description.hint')"
          :label="t('website.form.groups.description.label')"
          name="description"
        >
          <FormTextarea auto-resize />
        </FormControl>
      </FormRow>
    </FormSection>
    <FormSection :title="t('website.form.groups.contact.title')">
      <FormRow template="1fr 1fr">
        <FormControl :label="t('website.form.full_name')" name="name">
          <FormText />
        </FormControl>
        <FormControl :label="t('website.form.company')" name="company">
          <FormText />
        </FormControl>
      </FormRow>
      <FormRow template="1fr 1fr">
        <FormControl :label="t('website.form.email')" name="email">
          <FormText />
        </FormControl>
        <FormControl :label="t('website.form.phone')" name="phone">
          <FormText />
        </FormControl>
      </FormRow>
    </FormSection>
    <FormSection :title="t('website.form.groups.meeting_room.title')">
      <FormRow>
        <FormControl name="Meeting_rooms_needed__c">
          <FormCheckbox :options="meetingRoomsNeededOptions" />
        </FormControl>
      </FormRow>
      <FormRow v-if="showMeetingRoomInformationField">
        <FormControl
          :hint="t('website.form.groups.meeting_room_information.hint')"
          :label="t('website.form.groups.meeting_room_information.label')"
          name="MeetingroomInformation__c"
        >
          <FormTextarea auto-resize />
        </FormControl>
      </FormRow>
    </FormSection>
    <FormSection :title="t('website.form.groups.privacy.title')">
      <FormControl name="privacySelection">
        <FormRadio full-width-options :options="privacyOptions" />
      </FormControl>
    </FormSection>
  </BaseForm>
</template>
